import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar
} from '@mui/material';
import {
  Phone as PhoneIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as SuccessIcon
} from '@mui/icons-material';

const MetricsCards = ({ data }) => {
  // Handle case where data might be the entire response object
  const processedData = data?.data || data || {};
  
  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getSuccessRateColor = (rate) => {
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'warning';
    return 'error';
  };

  const metrics = [
    {
      title: 'Total Calls',
      value: processedData.totalCalls || 0,
      icon: PhoneIcon,
      color: 'primary',
      description: 'Total calls made',
      trend: processedData.totalCallsTrend || 0
    },
    {
      title: 'Call Duration',
      value: formatDuration(processedData.totalDuration || 0),
      icon: AccessTimeIcon,
      color: 'info',
      description: 'Total call time',
      trend: processedData.durationTrend || 0
    },
    {
      title: 'Success Rate',
      value: `${processedData.successRate || 0}%`,
      icon: SuccessIcon,
      color: getSuccessRateColor(processedData.successRate || 0),
      description: 'Completed calls',
      trend: processedData.successRateTrend || 0
    },
    {
      title: 'Total Cost',
      value: formatCurrency(processedData.totalCost || 0),
      icon: MoneyIcon,
      color: 'secondary',
      description: 'Total call costs',
      trend: processedData.costTrend || 0
    }
  ];

  return (
    <Grid container spacing={3}>
      {metrics.map((metric, index) => {
        const Icon = metric.icon;
        return (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card 
              elevation={2}
              sx={{ 
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  elevation: 4,
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box flex={1}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {metric.title}
                    </Typography>
                    <Typography variant="h4" component="div" fontWeight="bold" gutterBottom>
                      {metric.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {metric.description}
                    </Typography>
                    
                    {metric.trend !== 0 && (
                      <Box display="flex" alignItems="center" mt={1}>
                        <TrendingUpIcon 
                          sx={{ 
                            fontSize: 16, 
                            color: metric.trend > 0 ? 'success.main' : 'error.main',
                            mr: 0.5
                          }} 
                        />
                        <Typography 
                          variant="body2" 
                          color={metric.trend > 0 ? 'success.main' : 'error.main'}
                        >
                          {metric.trend > 0 ? '+' : ''}{metric.trend}% from last period
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  <Avatar 
                    sx={{ 
                      bgcolor: `${metric.color}.light`, 
                      color: `${metric.color}.main`,
                      width: 56,
                      height: 56
                    }}
                  >
                    <Icon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default MetricsCards; 